import { AppBskyFeedLike, AppBskyFeedPost, AppBskyFeedRepost, AtUri } from '@atproto/api'
import {
  bulkPutLikeHydration,
  bulkPutMediaItems,
  db,
  type GalleryVerb,
  getHydratedRkeys,
  type LikeHydrationRow,
  type MediaItemRow,
} from '../cache/db'

const APPVIEW = 'https://public.api.bsky.app'
const BATCH_URIS = 25
const PARALLEL_BATCHES = 16
const FLUSH_EVERY_N = 8

export type AnyEmbed = Record<string, unknown> & {
  $type?: string
  images?: { image?: unknown; alt?: string }[]
  video?: unknown
  alt?: string
  media?: AnyEmbed
}

export type AppViewPost = {
  uri: string
  cid?: string
  record?: {
    embed?: AnyEmbed
    reply?: { parent?: { uri?: string } }
    labels?: { $type?: string; values?: { val?: string }[] }
  }
  labels?: { val?: string }[]
  author?: { labels?: { val?: string }[] }
}

function blobLink(b: unknown): string | null {
  if (!b) return null
  if (typeof b === 'string') return b
  const o = b as { ref?: unknown }
  if (typeof o.ref === 'string') return o.ref
  if (o.ref && typeof o.ref === 'object') {
    const r = o.ref as { $link?: string }
    if (typeof r.$link === 'string') return r.$link
  }
  return null
}

export function extractMediaItems(
  embed: AnyEmbed | undefined,
  ownerHandle: string,
  did: string,
  rkey: string,
  likedAt: string,
  verb: GalleryVerb,
  labels: string[] | undefined,
  isReply: boolean,
  now: number,
): MediaItemRow[] {
  if (!embed) return []
  const out: MediaItemRow[] = []
  const visit = (e: AnyEmbed | undefined) => {
    if (!e) return
    const t = String(e.$type ?? '')
    if (t.startsWith('app.bsky.embed.images') && Array.isArray(e.images)) {
      for (const im of e.images) {
        const ref = blobLink(im.image)
        if (ref) {
          out.push({
            id: `${did}|${rkey}|${ref}|${verb}`,
            ownerHandle,
            did,
            rkey,
            cid: ref,
            alt: im.alt,
            likedAt,
            isVideo: false,
            verb,
            labels,
            isReply,
            fetched_at: now,
          })
        }
      }
    }
    if (t.startsWith('app.bsky.embed.video')) {
      const ref = blobLink(e.video)
      if (ref) {
        out.push({
          id: `${did}|${rkey}|${ref}|${verb}`,
          ownerHandle,
          did,
          rkey,
          cid: ref,
          alt: typeof e.alt === 'string' ? e.alt : undefined,
          likedAt,
          isVideo: true,
          verb,
          labels,
          fetched_at: now,
        })
      }
    }
    if (t.startsWith('app.bsky.embed.recordWithMedia') && e.media) visit(e.media)
  }
  visit(embed)
  return out
}

export function collectLabels(post: AppViewPost): string[] {
  const out = new Set<string>()
  for (const l of post.labels ?? []) {
    if (typeof l.val === 'string' && l.val) out.add(l.val)
  }
  for (const l of post.record?.labels?.values ?? []) {
    if (typeof l.val === 'string' && l.val) out.add(l.val)
  }
  for (const l of post.author?.labels ?? []) {
    if (typeof l.val === 'string' && l.val) out.add(l.val)
  }
  return Array.from(out)
}

export async function getPostsBatch(
  uris: string[],
  signal?: AbortSignal,
): Promise<Map<string, AppViewPost>> {
  const out = new Map<string, AppViewPost>()
  if (uris.length === 0) return out
  const u = new URL(`${APPVIEW}/xrpc/app.bsky.feed.getPosts`)
  for (const uri of uris.slice(0, BATCH_URIS)) u.searchParams.append('uris', uri)
  let attempt = 0
  while (attempt < 4) {
    if (signal?.aborted) return out
    try {
      const r = await fetch(u.toString(), {
        headers: { Accept: 'application/json' },
        signal,
      })
      if (r.status === 429 || r.status === 503) {
        const retry = Number(r.headers.get('retry-after') ?? '5')
        await new Promise((res) => setTimeout(res, Math.min(Math.max(retry, 1), 30) * 1000))
        attempt++
        continue
      }
      if (!r.ok) {
        attempt++
        await new Promise((res) => setTimeout(res, 500 * 2 ** attempt))
        continue
      }
      const data = (await r.json()) as { posts?: AppViewPost[] }
      for (const p of data.posts ?? []) out.set(p.uri, p)
      return out
    } catch (err) {
      if (signal?.aborted || (err instanceof DOMException && err.name === 'AbortError')) return out
      attempt++
      await new Promise((res) => setTimeout(res, 500 * 2 ** attempt))
    }
  }
  return out
}

function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = []
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size))
  return out
}

export type HydrateOptions = {
  signal?: AbortSignal
  onProgress?: (done: number, total: number) => void
}

/**
 * Process the user's own cached posts: extract media + self-labels inline.
 * No AppView round-trip — the embed is already in the record. Skips posts
 * already marked done in like_hydration. Writes media_items + like_hydration
 * in batches.
 */
export async function hydrateOwnPosts(
  handle: string,
  options: HydrateOptions = {},
): Promise<{ processed: number; mediaWritten: number }> {
  const { signal, onProgress } = options
  const [allOwnPosts, donePosted] = await Promise.all([
    db.records.where('[handle+collection]').equals([handle, 'app.bsky.feed.post']).toArray(),
    getHydratedRkeys(handle, 'posted'),
  ])
  const todo = allOwnPosts.filter((r) => !donePosted.has(r.rkey))
  const total = todo.length
  if (total === 0) return { processed: 0, mediaWritten: 0 }

  const now = Date.now()
  let pendingItems: MediaItemRow[] = []
  let pendingStatus: LikeHydrationRow[] = []
  let processed = 0
  let mediaWritten = 0

  const flush = async () => {
    if (pendingItems.length || pendingStatus.length) {
      const it = pendingItems
      const st = pendingStatus
      pendingItems = []
      pendingStatus = []
      mediaWritten += it.length
      await Promise.all([bulkPutMediaItems(it), bulkPutLikeHydration(st)])
    }
  }

  for (const r of todo) {
    if (signal?.aborted) break
    if (!AppBskyFeedPost.isRecord(r.value)) continue
    const at = (() => {
      try {
        return new AtUri(r.uri)
      } catch {
        return null
      }
    })()
    if (!at) continue
    const post = r.value
    const isReplyPost = !!post.reply?.parent?.uri
    const selfLabels = (
      (post.labels as { values?: { val?: string }[] } | undefined)?.values ?? []
    )
      .map((l) => l.val)
      .filter((v): v is string => typeof v === 'string' && v.length > 0)
    const media = extractMediaItems(
      post.embed as AnyEmbed | undefined,
      handle,
      at.hostname,
      at.rkey,
      post.createdAt,
      'posted',
      selfLabels.length > 0 ? selfLabels : undefined,
      isReplyPost,
      now,
    )
    pendingItems.push(...media)
    pendingStatus.push({
      id: `${handle}|posted|${r.rkey}`,
      ownerHandle: handle,
      likeRkey: r.rkey,
      verb: 'posted',
      status: media.length > 0 ? 'has-media' : 'no-media',
      fetched_at: now,
    })
    processed += 1
    if (processed % 200 === 0) {
      await flush()
      onProgress?.(processed, total)
    }
  }
  await flush()
  onProgress?.(processed, total)
  return { processed, mediaWritten }
}

type SubjectRecord = {
  rkey: string
  subjectUri: string
  whenAt: string
  verb: GalleryVerb
}

/**
 * Process unhydrated likes + reposts: batch AppView for the subject posts,
 * extract media + labels (AppView aggregate + record self-labels + author
 * labels), write to media_items + like_hydration.
 */
export async function hydrateLikesReposts(
  handle: string,
  options: HydrateOptions = {},
): Promise<{ processed: number; mediaWritten: number }> {
  const { signal, onProgress } = options
  const [allLikes, allReposts, doneLiked, doneReposted] = await Promise.all([
    db.records.where('[handle+collection]').equals([handle, 'app.bsky.feed.like']).toArray(),
    db.records.where('[handle+collection]').equals([handle, 'app.bsky.feed.repost']).toArray(),
    getHydratedRkeys(handle, 'liked'),
    getHydratedRkeys(handle, 'reposted'),
  ])
  const todo: SubjectRecord[] = []
  for (const r of allLikes) {
    if (doneLiked.has(r.rkey)) continue
    if (!AppBskyFeedLike.isRecord(r.value)) continue
    todo.push({
      rkey: r.rkey,
      subjectUri: r.value.subject.uri,
      whenAt: r.value.createdAt,
      verb: 'liked',
    })
  }
  for (const r of allReposts) {
    if (doneReposted.has(r.rkey)) continue
    if (!AppBskyFeedRepost.isRecord(r.value)) continue
    todo.push({
      rkey: r.rkey,
      subjectUri: r.value.subject.uri,
      whenAt: r.value.createdAt,
      verb: 'reposted',
    })
  }
  const total = todo.length
  if (total === 0) return { processed: 0, mediaWritten: 0 }

  const now = Date.now()
  const batches = chunk(todo, BATCH_URIS)
  let pendingItems: MediaItemRow[] = []
  let pendingStatus: LikeHydrationRow[] = []
  let processed = 0
  let mediaWritten = 0
  let completedBatches = 0

  const flush = async () => {
    if (pendingItems.length || pendingStatus.length) {
      const it = pendingItems
      const st = pendingStatus
      pendingItems = []
      pendingStatus = []
      mediaWritten += it.length
      await Promise.all([bulkPutMediaItems(it), bulkPutLikeHydration(st)])
    }
  }

  let cursor = 0
  const worker = async () => {
    while (!signal?.aborted) {
      const i = cursor++
      if (i >= batches.length) break
      const batch = batches[i]
      const uris = batch.map((b) => b.subjectUri)
      const got = await getPostsBatch(uris, signal)
      if (signal?.aborted) return
      for (const sub of batch) {
        const post = got.get(sub.subjectUri)
        const at = (() => {
          try {
            return new AtUri(sub.subjectUri)
          } catch {
            return null
          }
        })()
        const statusId = `${handle}|${sub.verb}|${sub.rkey}`
        if (!post || !at) {
          pendingStatus.push({
            id: statusId,
            ownerHandle: handle,
            likeRkey: sub.rkey,
            verb: sub.verb,
            status: 'error',
            fetched_at: now,
          })
          continue
        }
        const labelVals = collectLabels(post)
        const subjectIsReply = !!post.record?.reply?.parent?.uri
        const media = extractMediaItems(
          post.record?.embed,
          handle,
          at.hostname,
          at.rkey,
          sub.whenAt,
          sub.verb,
          labelVals.length > 0 ? labelVals : undefined,
          subjectIsReply,
          now,
        )
        pendingItems.push(...media)
        pendingStatus.push({
          id: statusId,
          ownerHandle: handle,
          likeRkey: sub.rkey,
          verb: sub.verb,
          status: media.length > 0 ? 'has-media' : 'no-media',
          fetched_at: now,
        })
        processed += 1
      }
      completedBatches += 1
      if (completedBatches % FLUSH_EVERY_N === 0) {
        await flush()
        onProgress?.(processed, total)
      }
    }
  }

  const workers: Promise<void>[] = []
  for (let i = 0; i < Math.min(PARALLEL_BATCHES, batches.length); i++) workers.push(worker())
  await Promise.all(workers)
  await flush()
  onProgress?.(processed, total)
  return { processed, mediaWritten }
}

async function countOwnPostsTodo(handle: string): Promise<number> {
  const [count, done] = await Promise.all([
    db.records.where('[handle+collection]').equals([handle, 'app.bsky.feed.post']).count(),
    getHydratedRkeys(handle, 'posted'),
  ])
  return Math.max(0, count - done.size)
}

async function countLikesRepostsTodo(handle: string): Promise<number> {
  const [likes, reposts, doneL, doneR] = await Promise.all([
    db.records.where('[handle+collection]').equals([handle, 'app.bsky.feed.like']).count(),
    db.records.where('[handle+collection]').equals([handle, 'app.bsky.feed.repost']).count(),
    getHydratedRkeys(handle, 'liked'),
    getHydratedRkeys(handle, 'reposted'),
  ])
  return Math.max(0, likes - doneL.size) + Math.max(0, reposts - doneR.size)
}

/**
 * Run both hydration passes back-to-back. Reports progress as a single
 * continuous bar (combined total of own-posts + likes/reposts work),
 * not two reset bars per phase. Capped at total so the % never exceeds 100.
 */
export async function hydrateAllForHandle(
  handle: string,
  options: HydrateOptions = {},
): Promise<{ processed: number; mediaWritten: number }> {
  const [ownTotal, subjTotal] = await Promise.all([
    countOwnPostsTodo(handle),
    countLikesRepostsTodo(handle),
  ])
  const grandTotal = ownTotal + subjTotal
  let phase1Done = 0
  const report = (processed: number) => {
    const cum = Math.min(grandTotal, phase1Done + processed)
    options.onProgress?.(cum, grandTotal)
  }
  const ownOpts: HydrateOptions = { signal: options.signal, onProgress: (p) => report(p) }
  const own = await hydrateOwnPosts(handle, ownOpts)
  phase1Done = own.processed
  if (options.signal?.aborted) return own
  const subjOpts: HydrateOptions = { signal: options.signal, onProgress: (p) => report(p) }
  const subj = await hydrateLikesReposts(handle, subjOpts)
  // Final tick at exact grand total so the bar lands on 100%.
  options.onProgress?.(grandTotal, grandTotal)
  return {
    processed: own.processed + subj.processed,
    mediaWritten: own.mediaWritten + subj.mediaWritten,
  }
}
